import React, { useState, useEffect } from "react";
import axios from "axios";

const calculateProgress = (data) => {
  if (!data || Object.keys(data).length === 0) {
    return 0;
  }

  let totalFields = 0;
  let filledFields = 0;

  const countFields = (obj) => {
    for (const key in obj) {
      if (Object.hasOwnProperty.call(obj, key)) {
        const value = obj[key];
        if (typeof value === "object" && value !== null) {
          countFields(value);
        } else if (value !== null && value !== undefined && value !== "") {
          filledFields++;
        }
        totalFields++;
      }
    }
  };

  countFields(data);

  return ((filledFields / totalFields) * 100).toFixed(0);
};

const ClinicalDataStatus = ({ record }) => {
  const recipient_id = record?.id;
  const [recipientClinicals, setRecipientClinicals] = useState(null);

  // If the record already has pre-computed clinical_progress from backend, use it directly (0 HTTP calls!)
  const hasPrecomputed = record?.clinical_progress !== undefined;

  useEffect(() => {
    // Only fetch from server if not already provided on the record
    if (hasPrecomputed || !recipient_id) return;

    let isMounted = true;
    const fetchFallback = async () => {
      try {
        let userToken = localStorage.getItem("token");
        let urlLink = `${process.env.REACT_APP_API_URL}/clinical/recipient/${recipient_id}`;
        const response = await axios.get(urlLink, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (isMounted) setRecipientClinicals(response.data);
      } catch (err) {
        // Fallback silently
      }
    };
    fetchFallback();
    return () => {
      isMounted = false;
    };
  }, [recipient_id, hasPrecomputed]);

  const rawProgress = hasPrecomputed
    ? record.clinical_progress
    : calculateProgress(recipientClinicals);
  const progress = Math.min(100, Math.max(0, parseInt(rawProgress, 10) || 0));

  // Determine status color and label
  const isComplete = progress === 100;
  const isPartial = progress > 0 && progress < 100;
  const barColor = isComplete
    ? "bg-emerald-500"
    : progress >= 50
    ? "bg-indigo-500"
    : isPartial
    ? "bg-amber-500"
    : "bg-gray-300";

  const textColor = isComplete
    ? "text-emerald-700 font-semibold"
    : isPartial
    ? "text-indigo-700 font-medium"
    : "text-gray-400";

  return (
    <div
      className="flex flex-col justify-center w-full leading-normal"
      title={`Clinical Profile: ${progress}% complete`}
    >
      <div
        className="text-sm font-semibold text-gray-800 truncate leading-tight"
        title={record?.name}
      >
        {record?.name || "Unnamed Recipient"}
      </div>
      <div className="flex items-center space-x-2 mt-1.5 w-full leading-none">
        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 rounded-full ${barColor}`}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className={`text-[11px] font-medium leading-none ${textColor}`}>
          {progress}%
        </span>
      </div>
    </div>
  );
};

export default ClinicalDataStatus;
